// Client-side WorkClient enforcing at-most-once mutation semantics.
//
// R2 contract (handoff 2026-10-12 claude-runtime-r2-session-protocols):
//   RuntimeApiClient (./runtimeClient.ts) auto-retries on 429 and
//   transient network errors. That is safe for reads but NOT for
//   mutations: a retried write could land twice at the vendor. The
//   WorkClient wraps the API client with a strict mutation path that:
//
//     1. Carries an idempotency key (UUID) generated at the moment the
//        user confirms intent. The same key is reused on EVERY retry.
//        The server (services/api-server/work-client.mjs) replays the
//        original envelope rather than re-executing the vendor call.
//     2. Disables the parent client's auto-retry for mutations. On a
//        429, network error, or AMBIGUOUS envelope the WorkClient
//        surfaces an explicit state to the UI. The UI chooses whether
//        to resubmit; a resubmission MUST reuse the same idempotency
//        key (fireMutation accepts an optional idempotencyKey arg to
//        enable this).
//     3. Reads (readResource) still delegate to RuntimeApiClient and
//        keep its exponential-backoff retry on 429/network errors.
//
// Why not just change RuntimeApiClient's retry policy?
//   The existing client is still consumed by the service-to-service
//   bridge where its retry behavior is acceptable (the dispatcher
//   layer at the server now enforces idempotency independently). The
//   WorkClient is a narrow wrapper for the embedded sales workspace
//   UI, where mutation at-most-once is a hard rule.

import {
  RuntimeApiError,
  type FetchImpl,
  type SleepImpl,
} from './runtimeClient.js';
import type {
  ActionRequestBody,
  ActionResponse,
  GateResponse,
  AuditQueryRequest,
  AuditQueryResponse,
} from './types.js';
import type { HubSpotSession } from '../auth/hubspot-handshake.js';
import { SignedRuntimeApiClient, contextHeaders } from './transport.js';

export const IDEMPOTENCY_HEADER = 'X-Idempotency-Key';

export type SessionAccessor = () => HubSpotSession | null;

export interface WorkClientOptions {
  baseUrl: string;
  getSession: SessionAccessor;
  fetchImpl?: FetchImpl;
  sleep?: SleepImpl;
  defaultTimeoutMs?: number;
  // Pluggable UUID generator so tests get deterministic keys. In the
  // browser the default is crypto.randomUUID().
  generateIdempotencyKey?: () => string;
  // Read-retry budget. Mutations always have 0.
  readMaxRetries?: number;
  retryBaseDelayMs?: number;
  retryMaxDelayMs?: number;
}

export type MutationState =
  | { kind: 'success'; response: ActionResponse }
  | { kind: 'ambiguous'; response: ActionResponse; idempotencyKey: string }
  | { kind: 'rate_limited'; retryAfterMs?: number; idempotencyKey: string }
  | { kind: 'network_error'; message: string; idempotencyKey: string }
  | { kind: 'validation_error'; errorCode: string; message: string }
  | { kind: 'forbidden'; errorCode: string; message: string }
  | { kind: 'auth_failed'; errorCode: string; message: string }
  | { kind: 'vendor_error'; errorCode: string; message: string; retryable: boolean };

export interface FireMutationArgs {
  actionName: string;
  body: ActionRequestBody;
  // Optional: pass a prior key to resubmit the SAME intent. The UI
  // calls this when the user explicitly taps "retry" after a
  // rate_limited or network_error state.
  idempotencyKey?: string;
}

export class WorkClient {
  private readonly readClient: SignedRuntimeApiClient;
  private readonly mutationClient: SignedRuntimeApiClient;
  private readonly generateKey: () => string;

  constructor(options: WorkClientOptions) {
    const commonOptions = {
      baseUrl: options.baseUrl,
      getSession: options.getSession,
      ...(options.fetchImpl !== undefined ? { fetchImpl: options.fetchImpl } : {}),
      ...(options.sleep !== undefined ? { sleep: options.sleep } : {}),
      ...(options.defaultTimeoutMs !== undefined
        ? { defaultTimeoutMs: options.defaultTimeoutMs }
        : {}),
      ...(options.retryBaseDelayMs !== undefined
        ? { retryBaseDelayMs: options.retryBaseDelayMs }
        : {}),
      ...(options.retryMaxDelayMs !== undefined
        ? { retryMaxDelayMs: options.retryMaxDelayMs }
        : {}),
    };
    this.readClient = new SignedRuntimeApiClient({
      ...commonOptions,
      maxRetries: options.readMaxRetries ?? 2,
    });
    this.mutationClient = new SignedRuntimeApiClient({
      ...commonOptions,
      // CRITICAL: mutations never auto-retry. The retry decision lives
      // in the UI, which must reuse the SAME idempotency key on resubmit.
      maxRetries: 0,
    });
    this.generateKey = options.generateIdempotencyKey ?? defaultGenerateKey;
  }

  /**
   * Fire a mutation with at-most-once semantics. Returns a tagged union
   * so the UI can surface a specific state without inferring from
   * exception types. Never throws under normal error conditions; a
   * network timeout or an auth failure both come back as a MutationState
   * so the UI never crashes a review screen.
   */
  async fireMutation(args: FireMutationArgs): Promise<MutationState> {
    const idempotencyKey = args.idempotencyKey ?? this.generateKey();
    try {
      const response = await this.mutationClient.request<ActionResponse>({
        method: 'POST',
        path: `/v1/actions/${args.actionName}`,
        body: args.body,
        headers: { [IDEMPOTENCY_HEADER]: idempotencyKey },
      });
      if (response.result === 'SUCCESS') {
        return { kind: 'success', response };
      }
      if (response.result === 'AMBIGUOUS') {
        return { kind: 'ambiguous', response, idempotencyKey };
      }
      if (response.result === 'GATE_OFF') {
        return { kind: 'success', response };
      }
      // Any other result shape falls through to a vendor_error state
      // with the server's advisory retryability.
      return {
        kind: 'vendor_error',
        errorCode: 'errorCode' in response && response.errorCode ? response.errorCode : 'VENDOR_ERROR',
        message:
          'errorMessage' in response && response.errorMessage
            ? response.errorMessage
            : 'Vendor returned a non-success envelope',
        retryable: false,
      };
    } catch (err) {
      return mapErrorToState(err, idempotencyKey);
    }
  }

  /**
   * Resubmit a mutation using a known idempotency key. The server will
   * replay the original envelope if the original request landed, or
   * execute once if it did not. Either way the vendor sees exactly one
   * write.
   */
  async retryMutation(args: FireMutationArgs & { idempotencyKey: string }): Promise<MutationState> {
    return this.fireMutation(args);
  }

  // -- read paths --
  async readGate(gateName: string): Promise<GateResponse> {
    return this.readClient.readGate(gateName);
  }

  async queryAudit(body: AuditQueryRequest): Promise<AuditQueryResponse> {
    return this.readClient.queryAudit(body);
  }

  /**
   * Low-level signed read with full-retry policy. Prefer the typed
   * helpers above when a shape exists.
   */
  async readPath<T>(path: string): Promise<T> {
    return this.readClient.request<T>({ method: 'GET', path });
  }
}

function defaultGenerateKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `wk-${crypto.randomUUID()}`;
  }
  // Fallback for environments without crypto.randomUUID.
  const rand = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
  return `wk-${Date.now().toString(36)}-${rand.slice(0, 24)}`;
}

function mapErrorToState(err: unknown, idempotencyKey: string): MutationState {
  if (err instanceof RuntimeApiError) {
    switch (err.kind) {
      case 'RATE_LIMITED':
      case 'RETRY_EXHAUSTED':
        return {
          kind: 'rate_limited',
          ...(err.retryAfterMs !== undefined ? { retryAfterMs: err.retryAfterMs } : {}),
          idempotencyKey,
        };
      case 'NETWORK_ERROR':
      case 'TIMEOUT':
        return {
          kind: 'network_error',
          message: err.message,
          idempotencyKey,
        };
      case 'AUTH_FAILED':
        return { kind: 'auth_failed', errorCode: err.errorCode ?? 'AUTH_FAILED', message: err.message };
      case 'FORBIDDEN':
        return { kind: 'forbidden', errorCode: err.errorCode ?? 'FORBIDDEN', message: err.message };
      case 'VALIDATION_ERROR':
        return {
          kind: 'validation_error',
          errorCode: err.errorCode ?? 'VALIDATION_ERROR',
          message: err.message,
        };
      case 'VENDOR_ERROR':
      case 'SERVER_ERROR':
      default:
        return {
          kind: 'vendor_error',
          errorCode: err.errorCode ?? err.kind,
          message: err.message,
          retryable: false,
        };
    }
  }
  return {
    kind: 'network_error',
    message: err instanceof Error ? err.message : 'Unknown failure',
    idempotencyKey,
  };
}

export function createWorkClient(options: WorkClientOptions): WorkClient {
  return new WorkClient(options);
}

export { contextHeaders };
